import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PlusCircle, GearSix } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AssetInstancesTable } from 'components/assets';
import { useAssetCategories, useAllMaintenanceSchedules, useMaintenanceEvents } from 'hooks/useAssets';
import { MaintenanceCalendar } from 'shared/components/maintenance/sections/MaintenanceCalendar';
import { Spinner } from 'components/ui/spinner';
import AssetStatisticsTab from './statistics/AssetStatisticsTab';
import VoorzieningenMapper from './mapper/VoorzieningenMapper';

const tabTriggerClass = "rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary text-muted-foreground hover:text-foreground px-4 py-2.5 font-medium";

/**
 * Dynamic page that shows assets for a specific category with tabs.
 */
const CategoryAssetsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { categories } = useAssetCategories();
  const { schedules, isLoading: isLoadingSchedules, refetch: refetchSchedules } = useAllMaintenanceSchedules();
  const { events, isLoading: isLoadingEvents, refetch: refetchEvents } = useMaintenanceEvents();

  const category = categories.find(c => (c as any).slug === slug);

  const refetchMaintenance = () => {
    refetchSchedules();
    refetchEvents();
  };

  // Filter schedules/events by category
  const categorySchedules = category
    ? schedules.filter((s: any) => s.asset?.assetCategoryId === category.id)
    : schedules;
  const categoryEvents = category
    ? events.filter((e: any) => e.asset?.assetCategoryId === category.id)
    : events;

  return (
    <div className="mx-auto px-4 py-6 max-w-[1600px]">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold">{category?.name || slug}</h1>
              {category && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => navigate(`/settings/voorzieningen-categorieen/${category.id}`, { state: { returnTo: `/assets/category/${slug}` } })}
                >
                  <GearSix className="h-4 w-4" />
                </Button>
              )}
            </div>
            {category?.description && (
              <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
            )}
          </div>
          <Button onClick={() => navigate('/assets/bulk-create')}>
            <PlusCircle className="h-4 w-4 mr-1" />
            Nieuwe Voorziening
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overzicht">
        <TabsList className="h-auto bg-transparent p-0 rounded-none border-b border-border w-full justify-start gap-0">
          <TabsTrigger value="overzicht" className={tabTriggerClass}>Overzicht</TabsTrigger>
          <TabsTrigger value="onderhoud" className={tabTriggerClass}>Onderhoudskalender</TabsTrigger>
          <TabsTrigger value="mapper" className={tabTriggerClass}>Hierarchische weergave</TabsTrigger>
          <TabsTrigger value="statistieken" className={tabTriggerClass}>Statistieken</TabsTrigger>
        </TabsList>

        <TabsContent value="overzicht" className="mt-6">
          <AssetInstancesTable categorySlug={slug} />
        </TabsContent>

        <TabsContent value="onderhoud" className="mt-6">
          {isLoadingSchedules || isLoadingEvents ? (
            <div className="flex items-center justify-center py-8">
              <Spinner className="h-5 w-5" />
              <span className="ml-2 text-sm text-muted-foreground">Onderhoud laden...</span>
            </div>
          ) : (
            <MaintenanceCalendar
              schedules={categorySchedules}
              events={categoryEvents}
              onRefetch={refetchMaintenance}
            />
          )}
        </TabsContent>

        <TabsContent value="mapper" className="mt-6">
          <VoorzieningenMapper />
        </TabsContent>

        <TabsContent value="statistieken" className="mt-6">
          <AssetStatisticsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CategoryAssetsPage;
